/**
 * Admin dashboard — landing page for role=admin users. Currently surfaces
 * "at a glance" warnings that need attention (domains expiring soon, failed
 * scheduler jobs, AI-spend anomalies). Task lists, per-user personalisation
 * and additional health signals will land here later.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertOctagon, Bot, CheckCircle2, Clock, DollarSign, ExternalLink, Loader2,
  ShieldCheck, TrendingUp,
} from "lucide-react";
import { DomainsAtRiskWidget } from "@/components/DomainsAtRiskWidget";

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [warn, setWarn] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/warnings")
      .then((r) => setWarn(r.data || null))
      .catch(() => setWarn(null))
      .finally(() => setLoading(false));
  }, []);

  const firstName = user?.first_name || user?.name?.split(" ")?.[0] || "";
  const greeting = getGreeting();

  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      <div>
        <div className="label-overline">Overview</div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight">
          {greeting}{firstName ? `, ${firstName}` : ""}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Anything needing your attention today shows up here — domains about to
          expire, failed integrations, cost spikes, and later your task list.
        </p>
      </div>

      <DomainsAtRiskWidget />

      {loading ? (
        <Card className="rounded-sm">
          <CardContent className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading warnings…
          </CardContent>
        </Card>
      ) : (
        <>
          <FailedJobsWidget failed={warn?.failed_jobs || []} navigate={navigate} />
          <AiSpendWidget ai={warn?.ai_spend} navigate={navigate} />
          <AllClearFallback warn={warn} />
        </>
      )}

      <PlaceholdersFooter />
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 5) return "Working late";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function FailedJobsWidget({ failed, navigate }) {
  if (!failed || failed.length === 0) return null;
  return (
    <Card className="rounded-sm border-red-200 bg-red-50/40"
      data-testid="failed-jobs-widget">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <AlertOctagon className="h-4 w-4 text-red-600" />
            <div>
              <div className="label-overline">Failed scheduler jobs (24h)</div>
              <div className="text-[11px] text-zinc-600 font-mono">
                <span className="text-red-600 font-medium">{failed.length}</span> job
                {failed.length !== 1 ? "s" : ""} errored in the last 24 hours
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]"
            onClick={() => navigate("/scheduler")}
            data-testid="failed-jobs-open-scheduler">
            Open scheduler <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        </div>
        <div className="border border-red-200/70 rounded-sm bg-white overflow-hidden">
          {failed.slice(0, 5).map((j) => (
            <div key={j.job_id}
              className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-zinc-100 items-center text-[11px]
              hover:bg-zinc-50/60 transition-colors last:border-b-0"
              data-testid={`failed-job-row-${j.job_id}`}>
              <div className="col-span-4 font-mono truncate">
                <div className="font-medium text-zinc-800 truncate">{j.name}</div>
                <div className="text-[9px] text-zinc-500 truncate">{j.job_id}</div>
              </div>
              <div className="col-span-2 font-mono text-[10px] text-zinc-500 flex items-center gap-1">
                <Clock className="h-3 w-3 shrink-0" />
                {new Date(j.started_at).toLocaleString("en-AU", {dateStyle: "short", timeStyle: "short"})}
              </div>
              <div className="col-span-5 font-mono text-[10px] text-red-700 truncate" title={j.error}>
                {j.error || "(no error message)"}
              </div>
              <div className="col-span-1 text-right">
                {j.still_failing ? (
                  <Badge variant="outline" className="rounded-sm text-[8px] bg-red-50 text-red-700 border-red-200">
                    still failing
                  </Badge>
                ) : (
                  <Badge variant="outline" className="rounded-sm text-[8px] bg-emerald-50 text-emerald-700 border-emerald-200">
                    recovered
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function AiSpendWidget({ ai, navigate }) {
  if (!ai) return null;
  const spike = ai.spike;
  // Also show the widget when things look normal — as a positive signal —
  // if there's genuine month-to-date usage.
  if (!spike && (!ai.mtd_cost || ai.mtd_cost < 1)) return null;
  const cls = spike?.severity === "critical"
    ? "border-red-200 bg-red-50/40"
    : spike ? "border-amber-200 bg-amber-50/40"
      : "border-emerald-200 bg-emerald-50/30";
  const icon = spike
    ? <TrendingUp className={`h-4 w-4 ${spike.severity === "critical" ? "text-red-600" : "text-amber-600"}`} />
    : <DollarSign className="h-4 w-4 text-emerald-600" />;
  return (
    <Card className={`rounded-sm ${cls}`} data-testid="ai-spend-widget">
      <CardContent className="p-4 space-y-1.5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {icon}
            <div>
              <div className="label-overline">AI spend</div>
              <div className="text-[11px] text-zinc-600 font-mono">
                {spike ? (
                  <>
                    <span className={spike.severity === "critical" ? "text-red-600 font-medium" : "text-amber-700 font-medium"}>
                      {spike.over_pct > 0 ? "+" : ""}{spike.over_pct}%
                    </span>
                    {" "}vs previous months — projected {" "}
                    <strong>${spike.projected_cost.toFixed(2)}</strong>{" "}
                    (avg ${spike.avg_previous.toFixed(2)}, MTD ${ai.mtd_cost.toFixed(2)})
                  </>
                ) : (
                  <>
                    On track — MTD <strong>${ai.mtd_cost.toFixed(2)}</strong>,
                    projected ${ai.projected_month_cost.toFixed(2)}
                    {ai.avg_previous_month_cost > 0 && (
                      <> vs ${ai.avg_previous_month_cost.toFixed(2)} avg</>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]"
            onClick={() => navigate("/ai-usage")}
            data-testid="ai-spend-open-costs">
            Open AI costs <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AllClearFallback({ warn }) {
  const noFailedJobs = (warn?.failed_jobs || []).length === 0;
  const noAiSpike = !warn?.ai_spend?.spike;
  const domainsAtRisk = warn?.domains_at_risk_summary?.total || 0;
  // Only show this "all clear" badge when nothing at all needs attention.
  if (!(noFailedJobs && noAiSpike && domainsAtRisk === 0)) return null;
  return (
    <Card className="rounded-sm border-emerald-200 bg-emerald-50/40"
      data-testid="all-clear-widget">
      <CardContent className="p-4 flex items-center gap-2.5">
        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
        <div>
          <div className="font-medium text-emerald-800 text-sm">All clear</div>
          <div className="text-[11px] text-emerald-700/80">
            No failed jobs, cost spikes or expiring domains right now. Nice work.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PlaceholdersFooter() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 opacity-60"
      data-testid="dashboard-placeholders">
      <PlaceholderCard icon={ShieldCheck} title="Broken integrations"
        subtitle="Coming soon: OAuth & API health per integration" />
      <PlaceholderCard icon={Bot} title="My tasks"
        subtitle="Coming soon: personal task list per admin" />
      <PlaceholderCard icon={DollarSign} title="Client budget alerts"
        subtitle="Coming soon: over/under-spend flags per client" />
    </div>
  );
}

function PlaceholderCard({ icon: Icon, title, subtitle }) {
  return (
    <div className="border border-dashed border-zinc-200 rounded-sm p-3 bg-white">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3.5 w-3.5 text-zinc-400" />
        <div className="label-overline text-zinc-500">{title}</div>
      </div>
      <p className="text-[11px] text-zinc-400">{subtitle}</p>
    </div>
  );
}
